import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { PurchasingExecutiveRecord } from './dto/purchasing-executive-record.model';
import { TransformedPurchasingExecutiveRecord } from './dto/transformed-pe-record.model';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { PurchasingExecutiveAssignmentFormDialogComponent } from '../purchasing-executive-assignment-form-dialog/purchasing-executive-assignment-form-dialog.component';
import { Subject, lastValueFrom, takeUntil } from 'rxjs';
import { TransferRfsDto } from './dto/transfer-rfs.model';
import { Store } from '@ngrx/store';
import { DropDownOptions, ExtendedKeycloakUser, MyPurchaseForm, StoreDropdowns, UserInformation } from 'src/app/shared/models/app-models';
import { TRANSFER_ICON, VOLUNTEER_ICON } from 'src/app/data/constants/Data';
import { PEAssignmentListResponse } from 'src/app/shared/models/database-models/RFS.model';
import { TagItemVendorService } from '../../mypurchase-form/rfs/tag-item-vendor.service';
import { SaveMyPurchase } from 'src/app/shared/models/database-models';
import { FormTransformerPipe } from 'src/app/shared/pipes/form-transformer/form-transformer.pipe';
import { RfsService } from '../../mypurchase-form/rfs/rfs.service';
import { updateWholeForm } from 'src/app/core/store/form.actions';

interface ExtendedDropdownOptions extends DropDownOptions {
    displayItemNumber: number;
}

@Component({
    selector: 'app-purchasing-executive-assignment-list',
    templateUrl: './purchasing-executive-assignment-list.component.html',
    styleUrls: ['./purchasing-executive-assignment-list.component.scss'],
})
export class PurchasingExecutiveAssignmentListComponent {
    @Output() closeSideNav = new EventEmitter<any>(true)
    @Input() docNo: string = ''
    @Input() peAssignmentListResponse?: PEAssignmentListResponse
    ELEMENT_DATA: PurchasingExecutiveRecord[] = [];
    //full list of personnel that can be assigned. a personnel might not have an item assigned to him or her
    personnelList: string[] = []
    itemIdMappings: Map<number, ExtendedDropdownOptions> = new Map<number, ExtendedDropdownOptions>();
    displayedColumns: string[] = ['purchasingExecutive', 'itemNumber', 'actions'];
    username: string = ''
    destroy$: Subject<boolean> = new Subject<boolean>
    dpOptions!: StoreDropdowns;
    KCUserList: ExtendedKeycloakUser[] = [];
    wasTransferCalled: boolean = false
    @Input() isLocked: boolean = true
    //TRANSFORMED_ELEMENT_DATA - what user sees in the table
    dataSource: MatTableDataSource<TransformedPurchasingExecutiveRecord> = new MatTableDataSource<TransformedPurchasingExecutiveRecord>();
    constructor(
        public dialog: MatDialog, 
        private backendApi: BackendApiService,     
        private formTransformer: FormTransformerPipe, 
        private tagItemVendorService: TagItemVendorService, 
        private iconRegistry: MatIconRegistry, 
        private rfsService: RfsService,
        private sanitizer: DomSanitizer,
        private store: Store<{
            dp: StoreDropdowns;
            user: UserInformation;
            form: MyPurchaseForm;
        }>
    ) {
        this.iconRegistry.addSvgIconLiteral('transfer', this.sanitizer.bypassSecurityTrustHtml(TRANSFER_ICON));
        this.iconRegistry.addSvgIconLiteral('volunteer', this.sanitizer.bypassSecurityTrustHtml(VOLUNTEER_ICON));
        this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((user: UserInformation) => {
            this.username = user.userName;
        })
        this.store
            .select('dp')
            .pipe(takeUntil(this.destroy$))
            .subscribe((dp: StoreDropdowns) => {
                this.dpOptions = dp;
                this.KCUserList = dp.UserList as ExtendedKeycloakUser[];
            });
        this.store
            .select('form')
            .pipe(takeUntil(this.destroy$))
            .subscribe((form: MyPurchaseForm) => {
                let itemMap: Map<number, ExtendedDropdownOptions> = new Map<number, ExtendedDropdownOptions>();
                for (const item of form.ItemList) {
                    itemMap.set(item.AddItems.PurchaseItemId!, { id: item.AddItems.itemId.toString(), displayItemNumber: item.AddItems.displayItemNumber, value: `${item.AddItems.displayItemNumber} - ${item.AddItems.ItemDescription && item.AddItems.ItemDescription.length > 50 ? item.AddItems.ItemDescription.slice(0, 50) + '...' : item.AddItems.ItemDescription || ''}` })
                }
                this.itemIdMappings = itemMap
            });
    }
    transformBackendItemIdsToFrontendItemIds(itemString: string): string {
        let results: string[] = []
        let items: string[] = itemString.split(',')
        for (let item of items) {
            let itemNo: number = Number(item.trim())
            if (this.itemIdMappings.has(itemNo)) {
                results.push(this.itemIdMappings.get(itemNo)!.displayItemNumber.toString())
            }
        }
        let finalResult = results.join(',')
        return finalResult
    }
    ngOnChanges(changes: SimpleChanges) {
        if (this.peAssignmentListResponse) {
            this.populateTable(this.peAssignmentListResponse)
        }
    }
    ngOnDestroy() {
        this.destroy$.next(true)
        this.destroy$.unsubscribe()
    }
    async ngOnInit() {
        if (this.peAssignmentListResponse) {
            this.populateTable(this.peAssignmentListResponse)
        }
    }
    getNameOfUser(username: string) {
        return this.KCUserList.find((user) => user.username == username)?.value ?? username;
    }
    populateTable(response: PEAssignmentListResponse) {
        this.personnelList = response.PurchaseExecutives.map(x => x.id)
        for (const [key, value] of Object.entries(response.AssignmentList)) {
            if (Array.isArray(value))
                for (const itemNo of value) {
                    this.ELEMENT_DATA.push({ purchasingExecutive: key, itemNumber: itemNo })
                }
        }
        const TRANSFORMED_ELEMENT_DATA = this.transformPurchasingExecutiveRecords(response.AssignmentList)
        this.dataSource = new MatTableDataSource<TransformedPurchasingExecutiveRecord>(TRANSFORMED_ELEMENT_DATA)
    }
    transformPurchasingExecutiveRecords(assignmentList: any) {
        const newArr: TransformedPurchasingExecutiveRecord[] = [];
        let personnelListCopy = structuredClone(this.personnelList)
        for (const [key, value] of Object.entries(assignmentList)) {
            personnelListCopy = personnelListCopy.filter(e => e !== key);
            if (Array.isArray(value))
                newArr.push({
                    purchasingExecutive: key,
                    itemNumbersString: value.join(','),
                    canTransfer: key == this.username,
                    canVolunteer: key !== this.username
                })
        }
        personnelListCopy.forEach(x => {
            newArr.push({
                purchasingExecutive: x,
                itemNumbersString: '',
                canTransfer: x == this.username,
                canVolunteer: x !== this.username
            })
        })
        return newArr
    }

    transfer(element: TransformedPurchasingExecutiveRecord) {
        const { isSuccess } =
          this.tagItemVendorService.validateAllowPEReassignment(this.docNo);

        // Prevent proceeding with transfer if there is tagging validation issue
        if (!isSuccess) {
          return;
        }

        const itemArray: number[] = element.itemNumbersString.split(',').map(x => Number(x)).filter(z => !!z)
        const record = this.ELEMENT_DATA.find(x => x.purchasingExecutive == element.purchasingExecutive)
        const dialogRef = this.dialog.open(PurchasingExecutiveAssignmentFormDialogComponent, {
            data: { record: record, peArray: this.personnelList.filter(x => x !== this.username), itemArray: itemArray, action: 'Transfer', itemIdMappings: this.itemIdMappings },
            // maxWidth: '100%',
            // position: {
            //     top: '50vh',
            //     left: '50vw'
            // },
            // panelClass: 'alignMiddle'
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) this.updateRecords(result, true)
        });
    }
    volunteer(element: TransformedPurchasingExecutiveRecord) {
        const { isSuccess } =
          this.tagItemVendorService.validateAllowPEReassignment(this.docNo);

        // Prevent proceeding with takeover if there is tagging validation issue
        if (!isSuccess) {
          return;
        }
        
        const itemArray: number[] = element.itemNumbersString.split(',').map(y => Number(y)).filter(z => !!z)
        const record = this.ELEMENT_DATA.find(x => x.purchasingExecutive == element.purchasingExecutive)
        const dialogRef = this.dialog.open(PurchasingExecutiveAssignmentFormDialogComponent, {
            data: { record: record, peArray: [this.username], itemArray: itemArray, action: 'Takeover', itemIdMappings: this.itemIdMappings },
            // maxWidth: '100%',
            // position: {
            //     top: '50vh',
            //     left: '50vw'
            // },
            // panelClass: 'alignMiddle'
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) this.updateRecords(result, false)
        });
    }
    closeMe() {
        this.closeSideNav.emit({ closeSideNav: true, wasTransferCalled: this.wasTransferCalled })
    }
    async updateRecords(result: any, isTransferring: boolean) {
        let requestObj: TransferRfsDto = {
            MPDocumentNumber: this.docNo,
            dagId: this.peAssignmentListResponse?.dagId!,
            actioner: this.username,
            PE: isTransferring ? result.purchasingExecutive : result.originalTaskOwner,
            workflowId: 'RFSApproval',
            items: result.itemNumbers
        }
        console.log(requestObj)
        try {
            if (isTransferring)
                await lastValueFrom(
                    this.backendApi.transferRFS(requestObj)
                );
            else
                await lastValueFrom(
                    this.backendApi.takeoverRFS(requestObj)
                );

            // Refresh assignment list, and later async repopulate table
            await this.rfsService.onFetchAssignmentList();

            // Refresh tagging for item-vendor
            let formResponse: SaveMyPurchase = await lastValueFrom(
              this.backendApi.getDraft(this.docNo)
            );
            let formData: MyPurchaseForm =
              this.formTransformer.transformInverse(
                formResponse,
                this.dpOptions
              );

            // Update form items' purchase executives, more than one item can be affected by transfer/takeover
            this.store.dispatch(updateWholeForm({ form: formData }));

            if (
              formData.documentNo &&
              (formData.TaggedGroups || formData.documentStatusId === 15)
            ) {
              this.tagItemVendorService.setTaggedGroups(
                formData.documentNo,
                formData.TaggedGroups ?? []
              );
            }

            this.wasTransferCalled = true
        }
        catch (error: unknown) {
            alert('Update failed')
        }
    }
}
